import type { Signal } from '@preact/signals-react';
import dot from 'dot-object';
import type { Dispatch, ReactNode, Ref } from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type {
  JsonDataWrapper,
  OdinDataRetrievalOptions,
  OdinGetDataFunction,
  OdinGetSubscriberFunction,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import type { TypeOrArray } from '../../common-interfaces';
import ContinuousScrollerDataLoadingIndicator from '../../continuous-scroller/continuous-scroller-data-loading-indicator.component';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import type { DropDownOptionRendererRef } from '../../data/drop-down-option-renderer/drop-down-option-renderer.component';
import DropDownOptionRenderer from '../../data/drop-down-option-renderer/drop-down-option-renderer.component';
import type { DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import { DataSearchLocation } from '../../enums';
import type { JsonData, JsonDataItem } from '../../generic-types';
import { OptionNavigator } from '../../navigation/option-navigator/option-navigator.component';
import TemplateOutlet from '../../template-outlet/template-outlet.component';
import type { DropDownReducerAction } from './drop-down.reducer';
import { DropDownActionType } from './drop-down.reducer';
import type { ActionSectionItem } from './drop-down-action-section.component';
import { dropDownOptionDomId } from './drop-down-common';
import type {
  DropDownOptionListTemplateProps,
  DropDownOptionListTemplateType,
  DropDownOptionTemplateType,
  DropDownResult,
} from './drop-down-types';
import { DropDownSelectionType } from './drop-down-types';

export interface DropDownOptionListProps extends DynamicFieldDataProps {
  ref?: Ref<DropDownOptionRendererRef>;
  id: string | number;
  value?: Array<DropDownResult>;
  setDefaultValue: boolean;
  selectionType?: DropDownSelectionType;
  useCheckboxForMultiSelect?: boolean;
  /**
   * I would prefer to have some kind of function that would allow the calling component to determine what to do on each
   * option component render but that seems highly inefficient (especially since the item being passed to the function
   * would be JSX and would not be able to be changed). So I'm using this setting to tell the component to remove any
   * options in the currently selected values when isMultiSelect is set to true. When using this setting
   * useCheckboxForMultiSelect should be set to false.
   */
  removeOnSelect?: boolean;
  isListOpen: boolean;
  itemsToRemove?: Array<DropDownResult>;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  filterLocation?: DataSearchLocation;
  registerSignal?: Signal<boolean>;
  dispatcher: Dispatch<DropDownReducerAction>;
  onChange?: (value?: TypeOrArray<DropDownResult>) => void;
  onDoubleClick?: (value: DropDownResult) => void;
  optionListStyle?: string;
  optionHoverStyle?: string;
  optionSelectedStyle?: string;
  optionTemplate?: DropDownOptionTemplateType;
  optionListTemplate?: DropDownOptionListTemplateType;
  dropDownDataLoaded?: (page: number, data: JsonData) => void;
  showAddNewOption?: boolean;
  allowDuplicateCall?: boolean;
  onNewItemCreated?: (value: JsonDataItem) => void;
  showDataOnSearch?: boolean;
  minimumSearchCharacters?: number;
  actionItems?: Array<ActionSectionItem>;
}

const DropDownOptionList = (props: DropDownOptionListProps) => {
  const {
    ref,
    id,
    textField,
    valueField,
    archivedField,
    data,
    value,
    setDefaultValue,
    selectionType = DropDownSelectionType.Single,
    useCheckboxForMultiSelect = true,
    removeOnSelect = false,
    isListOpen,
    itemsToRemove,
    filterSignal,
    filterLocation = DataSearchLocation.Local,
    registerSignal,
    dispatcher,
    onChange,
    onDoubleClick,
    optionListStyle,
    optionHoverStyle,
    optionSelectedStyle,
    optionTemplate,
    optionListTemplate,
    dropDownDataLoaded,
    pageSize = 50,
    showAddNewOption = false,
    allowDuplicateCall,
    onNewItemCreated,
    showDataOnSearch,
    minimumSearchCharacters,
    actionItems,
  } = props;

  const [scrollElement, setScrollElement] = useState<Element>();
  const [highlightedValue, setHighlightedValue] = useState<Array<DropDownResult>>();

  const currentItemData = useRef<JsonData | undefined>(undefined);
  const highlightedIndex = useRef<number | undefined>(undefined);
  const firstValue = useRef<DropDownResult | undefined>(undefined);
  const selectedValue = useRef<Array<DropDownResult> | undefined>(undefined);
  // screen height has to be at least twice the height of the dropdown scroller
  // so that there is enough space for the dropdown to go either up or down
  const maxDropDownScrollHeight = useRef(window.innerHeight > 800 ? '400px' : '200px');

  const isMultiSelect = useMemo(() => selectionType === DropDownSelectionType.Multiple, [selectionType]);
  const dataLoadIndicator = useMemo(() => <ContinuousScrollerDataLoadingIndicator />, []);

  const listboxId = useId();
  // checkbox-mode options expose interactive checkboxes instead of listbox/option semantics
  const isCheckboxMode = isMultiSelect && useCheckboxForMultiSelect;
  const activeDescendantId =
    !isCheckboxMode && highlightedValue && highlightedValue.length > 0
      ? dropDownOptionDomId(listboxId, highlightedValue[0].value)
      : undefined;

  useEffect(() => {
    selectedValue.current = value;
  }, [value]);

  const onKeyDown = (key: string, highlightedValue?: DropDownResult) => {
    if (key === 'Enter' || key === ' ') {
      if (highlightedValue) {
        onChangeFacade(highlightedValue);
      } else if (firstValue.current) {
        onChangeFacade(firstValue.current);
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dispatcher is stable, highlightedValue is not needed as trigger
  const onSingleItemSelected = useCallback(
    (item: DropDownResult) => {
      if (currentItemData.current) {
        dispatcher({
          type: DropDownActionType.SetSelectedValue,
          selectedValue: [
            {
              ...item,
            },
          ],
        });

        const selectedIndex = currentItemData.current?.findIndex(
          (dataItem) => dot.pick(valueField, dataItem) === item.value
        );
        highlightedIndex.current = selectedIndex > -1 ? selectedIndex : undefined;
      }

      if (highlightedValue && highlightedValue.length > 0) {
        setHighlightedValue(undefined);
      }

      onChange?.(item);
    },
    [valueField, onChange]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dispatcher is stable
  const onMultipleItemsSelected = useCallback(
    (items: Array<DropDownResult>) => {
      const nonSelectedItems = items.filter(
        (item) =>
          selectedValue.current === undefined || !selectedValue.current?.some((value) => value.value === item.value)
      );

      if (currentItemData.current) {
        if (items.length) {
          dispatcher({
            type: DropDownActionType.UpdateSelectedValue,
            selectedValue: [...items],
          });
        } else {
          dispatcher({ type: DropDownActionType.ClearSelectedValue });
        }
      }

      let newItems: Array<DropDownResult> = [];
      if (
        selectedValue.current &&
        selectedValue.current.length &&
        items.length &&
        items.length < (currentItemData.current?.length || 0)
      ) {
        if (
          selectedValue.current.findIndex((selectedItem) => items.some((item) => selectedItem.value === item.value)) >
          -1
        ) {
          newItems = selectedValue.current.filter((selectedItem) =>
            items.some((item) => selectedItem.value !== item.value)
          );
        } else {
          newItems = [...selectedValue.current, ...nonSelectedItems];
        }
      } else {
        if (nonSelectedItems.length) {
          newItems = [...(selectedValue.current || []), ...nonSelectedItems];
        }
      }

      selectedValue.current = newItems;
      onChange?.(newItems.length ? newItems : undefined);
    },
    [onChange]
  );

  const pageDataLoaded = (page: number, pageData: JsonData) => {
    if (pageData?.length > 0) {
      if (page === 1) {
        const result: DropDownResult = {
          text: dot.pick(textField, pageData[0]),
          value: dot.pick(valueField, pageData[0]),
          archived: archivedField ? !!dot.pick(archivedField, pageData[0]) : false,
          originalData: pageData[0],
        };

        firstValue.current = result;

        if (setDefaultValue && !selectedValue.current) {
          onSingleItemSelected(result);
        } else {
          highlightedIndex.current = -1;
        }
      }
    }

    dropDownDataLoaded?.(page, pageData);
  };

  const itemsChanged = (itemData: JsonData) => {
    // when the available items change the first value needs to be reset
    firstValue.current = {
      text: dot.pick(textField, itemData[0]),
      value: dot.pick(valueField, itemData[0]),
      archived: archivedField ? !!dot.pick(archivedField, itemData[0]) : false,
      originalData: itemData[0],
    };

    currentItemData.current = itemData;
    if (highlightedValue && highlightedValue.length === 1) {
      const newHighlightedIndex = itemData.findIndex(
        (item) => highlightedValue[0].value === dot.pick(valueField, item)
      );
      highlightedIndex.current = newHighlightedIndex > -1 ? newHighlightedIndex : undefined;
      if (highlightedIndex.current) {
        setHighlightedValue([
          {
            value: dot.pick(valueField, itemData[highlightedIndex.current]),
            text: dot.pick(textField, itemData[highlightedIndex.current]),
            archived: archivedField ? !!dot.pick(archivedField, itemData[highlightedIndex.current]) : false,
            originalData: itemData[highlightedIndex.current],
          },
        ]);
      }
    }

    if (ref && 'current' in ref && ref.current && data && Array.isArray(data)) {
      ref.current.resetDropDownHeight();
    }
  };

  const onScrollElementCreated = (element: HTMLElement) => {
    const convertedElement = element as Element;

    if (convertedElement) {
      setScrollElement(convertedElement);
    }
  };

  const onHighlightedValueChanged = (value: DropDownResult, index: number) => {
    setHighlightedValue([value]);
    highlightedIndex.current = index;
  };

  const scrollerData = useCallback<OdinGetDataFunction>(
    (scrollerSubscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (data && !Array.isArray(data)) {
        const scrollerOptions: OdinDataRetrievalOptions = {
          ...options,
          fieldId: id,
          fieldType: 'COMBOBOX',
          pageSize,
        };

        data?.getData?.(scrollerSubscriber, scrollerOptions);
      }
    },
    [id, data, pageSize]
  );

  const getScrollerSubscriber = useCallback<OdinGetSubscriberFunction>(
    (subscriber: OdinDataSender<JsonDataWrapper>) => {
      if (data && !Array.isArray(data)) {
        data.getSubscriber?.(subscriber, 'COMBOBOX');
      }
    },
    [data]
  );

  const onChangeFacade = useCallback(
    (value: TypeOrArray<DropDownResult>) => {
      if (!isMultiSelect) {
        const item = !Array.isArray(value) ? value : value[0];
        onSingleItemSelected(item);
      } else {
        const item = !Array.isArray(value) ? [value] : value;
        onMultipleItemsSelected(item);
      }
    },
    [isMultiSelect, onSingleItemSelected, onMultipleItemsSelected]
  );

  const optionListTemplateProps: DropDownOptionListTemplateProps = {
    id,
    textField,
    valueField,
    value: selectedValue.current,
    setDefaultValue,
    isMultiSelect,
    useCheckboxForMultiSelect,
    removeOnSelect,
    isListOpen,
    onChange,
    optionHoverStyle,
    optionSelectedStyle,
    scrollElement,
  };

  const optionListDefaultTemplate = useCallback<DropDownOptionListTemplateType>(
    (_props: DropDownOptionListTemplateProps, children?: TypeOrArray<ReactNode>) => <>{children}</>,
    []
  );

  return (
    <OptionNavigator
      valueField={valueField}
      textField={textField}
      archivedField={archivedField}
      itemsToRemove={itemsToRemove}
      removeOnSelect={removeOnSelect}
      selectedValue={value}
      registerOnFocus={false}
      registerSignal={registerSignal}
      keyDown={onKeyDown}
      highlightedValueChanged={onHighlightedValueChanged}
      scrollElementCreated={onScrollElementCreated}
      dataChanged={itemsChanged}
      pageDataLoaded={pageDataLoaded}
      change={onChangeFacade}
      role={isCheckboxMode ? undefined : 'listbox'}
      activeDescendantId={activeDescendantId}
      multiselectable={!isCheckboxMode && isMultiSelect ? true : undefined}
    >
      {({ onDataChanged, onPageDataLoaded, onChange }) => (
        <TemplateOutlet props={optionListTemplateProps} template={optionListTemplate || optionListDefaultTemplate}>
          <DropDownOptionRenderer
            ref={ref}
            id={id}
            textField={textField}
            valueField={valueField}
            archivedField={archivedField}
            data={data}
            value={selectedValue.current}
            setDefaultValue={setDefaultValue}
            highlightedValue={highlightedValue}
            optionIdPrefix={listboxId}
            selectionType={selectionType}
            useCheckboxForMultiSelect={useCheckboxForMultiSelect}
            removeOnSelect={removeOnSelect}
            itemsToRemove={itemsToRemove}
            allowDuplicateCall={allowDuplicateCall}
            onChange={onChange}
            onDoubleClick={onDoubleClick}
            optionSelectedStyle={optionSelectedStyle}
            optionHoverStyle={optionHoverStyle}
            className={optionListStyle}
            optionTemplate={optionTemplate}
            scrollElement={scrollElement}
            getData={scrollerData}
            getSubscriber={getScrollerSubscriber}
            filterSignal={filterSignal}
            filterLocation={filterLocation}
            dataLoadInspector={onPageDataLoaded}
            dataLoadIndicator={dataLoadIndicator}
            itemsChanged={onDataChanged}
            scrollerStyles={{ maxHeight: maxDropDownScrollHeight.current }}
            pageSize={pageSize}
            showAddNewOption={showAddNewOption}
            onNewItemCreated={onNewItemCreated}
            isListOpen={isListOpen}
            showDataOnSearch={showDataOnSearch}
            minimumSearchCharacters={minimumSearchCharacters}
            actionItems={actionItems}
          />
        </TemplateOutlet>
      )}
    </OptionNavigator>
  );
};

export default DropDownOptionList;
